import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { airtableService } from './services/airtable';
import { notificationService } from './services/notificationService';
import StudentLogin from './components/StudentLogin';
import StudentQueue from './components/StudentQueue';
import DrGrantLogin from './components/DrGrantLogin';
import DrGrantDashboard from './components/DrGrantDashboard';
import StudentEvaluation from './components/StudentEvaluation';
import SessionSummary from './components/SessionSummary';
import SessionHistory from './components/SessionHistory';
import TestConnection from './components/TestConnection';
import RouteDebug from './components/RouteDebug';
import WelcomePage from './components/WelcomePage';
import QueueBoard from './components/QueueBoard';
import Footer from './components/Footer';

function App() {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);

  // Debug logging
  console.log('App rendered with:', { 
    user: user?.name, 
    userType, 
    currentURL: window.location.href,
    pathname: window.location.pathname 
  });

  useEffect(() => {
    // Initialize Airtable on app startup so it's ready even when
    // users are restored from localStorage and skip the login page
    airtableService.loadSavedConfig();

    const savedUser = localStorage.getItem('notesncodes_user');
    const savedUserType = localStorage.getItem('notesncodes_userType');
    const savedSession = localStorage.getItem('notesncodes_session');

    if (savedUser && savedUserType) {
      setUser(JSON.parse(savedUser));
      setUserType(savedUserType);
    }

    if (savedSession) {
      setCurrentSession(JSON.parse(savedSession));
    }

    // Initialize notification service
    if (notificationService.isNotificationSupported()) {
      notificationService.initialize().catch(error => {
        console.error('Failed to initialize notification service:', error);
      });
    }

    // Re-hydrate state when the app regains focus (fixes iOS/mobile app-switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('App regained focus, re-hydrating state...');
        // Re-initialize Airtable connection
        airtableService.loadSavedConfig();

        // Restore user/session from localStorage in case React state was lost
        const restoredUser = localStorage.getItem('notesncodes_user');
        const restoredUserType = localStorage.getItem('notesncodes_userType');
        const restoredSession = localStorage.getItem('notesncodes_session');

        if (restoredUser && restoredUserType) {
          setUser(JSON.parse(restoredUser));
          setUserType(restoredUserType);
        }
        if (restoredSession) {
          setCurrentSession(JSON.parse(restoredSession));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleLogin = (userData, type) => {
    setUser(userData);
    setUserType(type);
    localStorage.setItem('notesncodes_user', JSON.stringify(userData));
    localStorage.setItem('notesncodes_userType', type);
  };

  const handleLogout = () => {
    setUser(null);
    setUserType(null);
    setCurrentSession(null);
    localStorage.removeItem('notesncodes_user');
    localStorage.removeItem('notesncodes_userType');
    localStorage.removeItem('notesncodes_session');
  };

  const handleSessionUpdate = (session) => {
    setCurrentSession(session);
    if (session) {
      localStorage.setItem('notesncodes_session', JSON.stringify(session));
    } else {
      localStorage.removeItem('notesncodes_session');
    }
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/" 
            element={
              user ? (
                userType === 'student' ? (
                  <Navigate to="/student/queue" />
                ) : (
                  <Navigate to="/dr-grant/dashboard" />
                )
              ) : (
                <WelcomePage />
              )
            } 
          />
          
          <Route 
            path="/student/login" 
            element={
              user && userType === 'student' ? (
                <Navigate to="/student/queue" />
              ) : (
                <>
                  {console.log('Rendering StudentLogin component')}
                  <StudentLogin onLogin={handleLogin} />
                </>
              )
            } 
          />
          
          <Route 
            path="/student/queue" 
            element={
              user && userType === 'student' ? (
                <StudentQueue 
                  user={user} 
                  currentSession={currentSession}
                  onLogout={handleLogout} 
                />
              ) : (
                <Navigate to="/student/login" />
              )
            } 
          />
          
          <Route 
            path="/dr-grant/login" 
            element={
              user && userType === 'doctor' ? (
                <Navigate to="/dr-grant/dashboard" />
              ) : (
                <>
                  {console.log('Rendering DrGrantLogin component')}
                  <DrGrantLogin onLogin={handleLogin} />
                </>
              )
            } 
          />
          
          <Route 
            path="/dr-grant/dashboard" 
            element={
              user && userType === 'doctor' ? (
                <DrGrantDashboard 
                  user={user} 
                  currentSession={currentSession}
                  onSessionUpdate={handleSessionUpdate}
                  onLogout={handleLogout} 
                />
              ) : (
                <Navigate to="/dr-grant/login" />
              )
            } 
          />
          
          <Route 
            path="/dr-grant/evaluate/:studentId" 
            element={
              user && userType === 'doctor' ? (
                <StudentEvaluation 
                  user={user} 
                  currentSession={currentSession}
                />
              ) : (
                <Navigate to="/dr-grant/login" />
              )
            } 
          />
          
          <Route
            path="/dr-grant/summary"
            element={
              user && userType === 'doctor' ? (
                <SessionSummary
                  user={user}
                  currentSession={currentSession}
                />
              ) : (
                <Navigate to="/dr-grant/login" />
              )
            }
          />

          <Route
            path="/dr-grant/summary/:sessionId"
            element={
              user && userType === 'doctor' ? (
                <SessionSummary
                  user={user}
                  currentSession={currentSession}
                />
              ) : (
                <Navigate to="/dr-grant/login" />
              )
            }
          />

          <Route
            path="/dr-grant/history"
            element={
              user && userType === 'doctor' ? (
                <SessionHistory />
              ) : (
                <Navigate to="/dr-grant/login" />
              )
            }
          />
          
          {/* Public wallboard for clinic screens: shows only the queue count */}
          <Route
            path="/board"
            element={<QueueBoard />}
          />

          <Route
            path="/test"
            element={<TestConnection />}
          />
          
          <Route 
            path="/debug" 
            element={<RouteDebug />} 
          />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

export default App;